import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { endpoints } from "../../../hooks/endpoints";
import { getToken, isTeacher } from "../../../hooks/utils";

interface CountdownTimerProps {
  targetDate: string; // ISO format like "2025-04-14T18:00:00"
  sessionId?: number | string;
}

const CountdownTimer: React.FC<CountdownTimerProps> = ({
  targetDate,
  sessionId,
}) => {
  const navigate = useNavigate();
  const { schoolgrade, group } = useParams();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const calculateTimeLeft = () => {
    const difference = +new Date(targetDate) - +new Date();
    let timeLeft = {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
    };

    if (difference > 0) {
      timeLeft = {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60),
      };
    }

    return timeLeft;
  };

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  const { days, hours, minutes, seconds } = timeLeft;

  const shouldStartNow = new Date(targetDate) < new Date();

  const shouldStartText = isTeacher()
    ? "طلابك في إنتظارك حتى تبدأ الحصة"
    : "برجاء الإنتظار حتى يدخل المدرس للحصة";

  const startLiveSession = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      if (!sessionId) return;
      try {
        setIsLoading(true);
        const res = await fetch(endpoints.startLiveSession, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: getToken(),
          },
          body: JSON.stringify({
            lesson_id: sessionId,
          }),
        });

        if (!res.ok) {
          setError("حدث خطأ أثناء بدء الحصة");
        }
      } catch (e) {
        console.error(e.message);
      } finally {
        setIsLoading(false);
      }
    },
    [sessionId]
  );

  return (
    <>
      <div className="fade-in m-0 p-4 w-full h-screen grow bg-ea-base flex flex-col items-center justify-center">
        <Button
          className="m-0 p-0 fixed"
          style={{ top: "2rem", left: "2rem", width: "4rem", height: "3rem" }}
          onClick={() => navigate(`/live`)}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="40"
            height="40"
            fill="white"
            className="bi bi-arrow-left"
            viewBox="0 0 16 16"
          >
            <path
              fillRule="evenodd"
              d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8"
            />
          </svg>
        </Button>
        <h2
          className={cn(
            "text-center mb-4 text-xl",
            shouldStartNow
              ? "text-primary bg-teal-50 border border-(--primary-color-light) p-4 rounded-2xl"
              : ""
          )}
        >
          {shouldStartNow ? shouldStartText : "تبدأ الحصة بعد 🔴"}
        </h2>
        {!shouldStartNow && (
          <div className="flex justify-center gap-3 text-center mb-4">
            <div className="p-3 border rounded-md bg-white aspect-square">
              <h4 className="mb-0">{days}</h4>
              <small className="text-gray-500">يوم</small>
            </div>
            <div className="p-3 border rounded-md bg-white aspect-square">
              <h4 className="mb-0">{hours}</h4>
              <small className="text-gray-500">ساعة</small>
            </div>
            <div className="p-3 border rounded-md bg-white aspect-square">
              <h4 className="mb-0">{minutes}</h4>
              <small className="text-gray-500">دقيقة</small>
            </div>
            <div className="p-3 border rounded-md bg-white aspect-square">
              <h4 className="mb-0">{seconds}</h4>
              <small className="text-gray-500">ثانية</small>
            </div>
          </div>
        )}
        {isTeacher() && (
          <div
            className={`flex flex-col justify-center items-center gap-3 text-center ${
              isLoading ? "disabled" : ""
            }`}
          >
            <Button onClick={startLiveSession} disabled={isLoading}>
              إبدأ الحصة الآن
            </Button>
            {error && (
              <p className="text-red-600 border border-red-400 bg-red-100 p-2 rounded-lg">
                {error}
              </p>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default CountdownTimer;
